import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import MicIcon from '@mui/icons-material/Mic';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from 'react-router-dom';
import xcentricService from '../services/xcentricService';
import type { XcentricWorklistItem, XcentricWorklistResponse } from '../services/xcentricService';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const STATUS_COLORS: Record<string, ChipColor> = {
  pending: 'default',
  in_progress: 'info',
  dictated: 'warning',
  finalized: 'success',
  distributed: 'success',
};

const WRITEBACK_LABELS: Record<string, { label: string; color: ChipColor }> = {
  sent: { label: 'Delivered to Xcentic', color: 'success' },
  acknowledged: { label: 'Delivered to Xcentic', color: 'success' },
  pending: { label: 'Sending…', color: 'info' },
  failed: { label: 'Delivery failed', color: 'error' },
  skipped: { label: 'Superseded', color: 'default' },
};

const FILTERS = [
  { value: 'actionable', label: 'To read' },
  { value: 'all', label: 'All studies' },
];

const ACTIONABLE = new Set(['pending', 'in_progress', 'dictated']);

const PacsWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<XcentricWorklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('actionable');
  const [days, setDays] = useState(7);
  const [connection, setConnection] = useState<{ ok: boolean; message: string } | null>(null);
  const [toast, setToast] = useState<{ sev: 'success' | 'error' | 'info'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await xcentricService.getWorklist());
    } catch {
      setToast({ sev: 'error', msg: 'Could not load the PACS worklist.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    xcentricService.testConnection()
      .then((r) => setConnection({ ok: r.success, message: r.message }))
      .catch(() => setConnection({ ok: false, message: 'Xcentic connection check failed' }));
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await xcentricService.syncWorklist(days);
      setToast({
        sev: r.failed ? 'info' : 'success',
        msg: `Xcentic: ${r.found} found, ${r.created} new${r.failed ? `, ${r.failed} could not be imported` : ''}.`,
      });
      await load();
    } catch {
      setToast({ sev: 'error', msg: 'Sync with Xcentic failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDictate = (item: XcentricWorklistItem) => {
    navigate(`/transcribe?session=${item.session_id}&study_id=${item.study_id}&launched_via=worklist`);
  };

  const handleResend = async (item: XcentricWorklistItem) => {
    if (!item.report_id) return;
    try {
      const r = await xcentricService.retryWriteback(item.report_id);
      setToast({ sev: 'success', msg: r.duplicate ? 'Xcentic already had this report.' : 'Report delivered to Xcentic.' });
      await load();
    } catch {
      setToast({ sev: 'error', msg: 'Report could not be delivered to Xcentic.' });
    }
  };

  const items = (data?.items ?? []).filter((i) => filter === 'all' || ACTIONABLE.has(i.status));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" component="h1">PACS Worklist</Typography>
          <Typography variant="body2" color="text.secondary">
            Studies handed over from Xcentic PACS. Dictate in RadNeo; finalized reports return to Xcentic automatically.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {connection && (
            <Tooltip title={connection.message}>
              <Chip
                size="small"
                color={connection.ok ? 'success' : 'error'}
                label={connection.ok ? 'Xcentic connected' : 'Xcentic unreachable'}
                data-testid="xcentic-connection"
              />
            </Tooltip>
          )}
          {data && <Chip size="small" variant="outlined" label={`Mode: ${data.integration_mode}`} />}
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
          <TextField
            select size="small" label="Show" value={filter}
            onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 150 }}
          >
            {FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Sync window" value={days}
            onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 150 }}
          >
            {[1, 7, 14, 31].map((d) => <MenuItem key={d} value={d}>Last {d} day{d > 1 ? 's' : ''}</MenuItem>)}
          </TextField>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync from Xcentic
          </Button>
          {data?.worklist_sync_enabled && (
            <Typography variant="caption" color="text.secondary">Automatic background sync is on.</Typography>
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">
          No {filter === 'actionable' ? 'studies waiting to be read' : 'Xcentic studies'} yet. Use “Sync from Xcentic”
          to pull the latest worklist, or open a study from the Xcentic portal with “Open in RadNeo”.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="PACS worklist">
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Study</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Report</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const wb = item.writeback_status ? WRITEBACK_LABELS[item.writeback_status] : null;
                return (
                  <TableRow key={item.study_id} hover data-testid={`worklist-row-${item.sr_pacs_id}`}>
                    <TableCell>
                      {item.study_priority ? (
                        <Chip size="small" label={item.study_priority}
                          color={item.study_priority === 'STAT' ? 'error' : item.study_priority === 'URGENT' ? 'warning' : 'default'} />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.patient_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">MRN {item.patient_mrn || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{[item.modality, item.study_description].filter(Boolean).join(' · ') || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.accession_number || item.sr_pacs_id} · PACS {item.sr_pacs_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.study_date || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={item.status.replace('_', ' ')} color={STATUS_COLORS[item.status] ?? 'default'} />
                      {item.claimed_by && (
                        <Typography variant="caption" display="block" color="text.secondary">Reading: {item.claimed_by}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.report_status ? (
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip size="small" variant="outlined" label={item.report_status === 'final' ? 'Final' : 'Draft'} />
                          {wb && (
                            <Tooltip title={item.writeback_error || ''}>
                              <Chip size="small" label={wb.label} color={wb.color} />
                            </Tooltip>
                          )}
                        </Stack>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="contained" startIcon={<MicIcon />}
                          onClick={() => handleDictate(item)} disabled={!item.session_id}>
                          {item.report_status === 'final' ? 'Open' : 'Dictate'}
                        </Button>
                        <Tooltip title={item.viewer_url ? 'Open images in the Xcentic viewer' : 'Viewer not configured'}>
                          <span>
                            {item.viewer_url ? (
                              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                                href={item.viewer_url} target="_blank" rel="noopener noreferrer">
                                Images
                              </Button>
                            ) : (
                              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} disabled>
                                Images
                              </Button>
                            )}
                          </span>
                        </Tooltip>
                        {item.writeback_status === 'failed' && item.report_id && (
                          <Button size="small" color="error" startIcon={<ReplayIcon />} onClick={() => handleResend(item)}>
                            Resend
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity={toast?.sev ?? 'info'} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PacsWorklistPage;
